import { UseQueryResult, useQueries, useQuery } from "react-query";
import { propelyApi } from "../../libs/firebase/api";
import { TRawUser, IApiUser, TApiUser, IRawUser } from "@propely/propely-typings";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { z } from "zod";
import { useRealtimeDocumentData } from "../generic/useRealtimeDocumentData";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { getAvatarColorForUser } from "@src/utils/colors";
import { queryClient } from "@src/index";

interface IUserWithImage extends IRawUser {
  imageUrl: string | undefined;
  avatarColor: string;
}

// Method to get a user fetch key
export const useUserByIdKey = (id: string | undefined) => ["users", { id }];

// Method to fetch a user
export const fetchUser = async (id: string | undefined) => {
  if (!id) {
    return undefined;
  }
  const user = await getDoc(doc(getFirestore(), "users", id));
  return TRawUser.parse({
    id: user.id,
    ...user.data(),
  });
};

export const useUserByID = (id: string | undefined) => {
  const $user = useQuery(useUserByIdKey(id), () => fetchUser(id));
  return {
    $user,
    user: $user.data,
  };
};

export const useUsersByIDs = (ids: string[]) => {
  const $users = useQueries(
    ids.map((id) => ({
      queryKey: useUserByIdKey(id),
      queryFn: () => fetchUser(id),
    }))
  );

  const users = $users.map(($user) => $user.data).filter((data): data is IRawUser => Boolean(data));
  const isLoading = $users.some(($user) => $user.isLoading);

  return {
    $users,
    users,
    isLoading,
  };
};

export const useUsersWithImageByIDs = (ids: string[]) => {
  const $users = useQueries(
    ids.map((id) => ({
      queryKey: useUserByIdKey(id),
      queryFn: async () => {
        const user = await fetchUser(id);
        const avatarColor = getAvatarColorForUser(user);
        const path = user?.profileImages?.small?.path ?? "";
        if (!path) {
          return { ...user, imageUrl: undefined, avatarColor };
        }
        const storage = getStorage();
        const reference = ref(storage, path);
        try {
          const downloadUrl = await getDownloadURL(reference);
          return { ...user, imageUrl: downloadUrl, avatarColor };
        } catch (e) {
          console.log("Error fetching profile picture url: ", e);
          return { ...user, imageUrl: undefined, avatarColor };
        }
      },
    }))
  );

  const users = $users.map(($user) => $user.data).filter((data): data is IUserWithImage => Boolean(data));
  const isLoading = $users.some(($user) => $user.isLoading);

  return {
    $users,
    users,
    isLoading,
  };
};

const TApiUsersResponse = z.object({
  datas: z.array(TApiUser),
});

const fetchUsersFromAPI = async (): Promise<IApiUser[]> => {
  const result = await propelyApi().get("/users");
  const users = TApiUsersResponse.parse(result.data);
  return users.datas;
};

export const useAPIUsersKey = () => ["users"];
export const useAPIUsers = (): UseQueryResult<IApiUser[], Error> => {
  return useQuery<IApiUser[], Error>(useAPIUsersKey(), fetchUsersFromAPI, { enabled: true });
};

export const prefetchAPIUsers = async () => {
  const users = await fetchUsersFromAPI();
  queryClient.setQueryData(useAPIUsersKey(), users);
};

export const getAllUsersFromCache = (): IApiUser[] => {
  const users = queryClient.getQueryData<IApiUser[]>(useAPIUsersKey());
  if (!users) {
    return [];
  }
  return users;
};

export const useRealtimeUserByID = (id: string | undefined) => {
  return useRealtimeDocumentData("users", TRawUser, id);
};

/**
 * @deprecated use useUsersByID instead and do image fetching in the component
 */
export const useUsersByIdsWithImages = (input: { userIDs: string[] }) => {
  const $users = useQuery(
    ["users", { userIDs: input.userIDs }],
    async () => {
      const results = await Promise.all(input.userIDs.map((id) => fetchUser(id)));
      const users = results.map((result) => TRawUser.parse(result));

      const usersWithImages = await Promise.all(
        users.map(async (user) => {
          if (!user.profileImages?.small?.path) {
            return user;
          }
          const reference = ref(getStorage(), user.profileImages?.small?.path);
          let downloadUrl;
          try {
            downloadUrl = await getDownloadURL(reference);
          } catch (e) {
            console.log("Error fetching profile picture url: ", e);
          }
          if (!downloadUrl) {
            return user;
          }
          console.log("downloadUrl", downloadUrl);
          return { ...user, profileImages: { ...user.profileImages, small: { ...user.profileImages?.small, downloadUrl } } };
        })
      );

      return { users, usersWithImages };
    },
    { enabled: true }
  );
  const users = $users.data?.users;
  const usersWithImages = $users.data?.usersWithImages;

  return {
    users,
    usersWithImages,
    $users,
  };
};
